/**
 * Intents: what the editor asks for, turned into the command that does it.
 *
 * A command carries everything needed to replay it — the new node's id, its order key among its
 * siblings, the timestamp. None of that is knowable from "the user pressed Tab", and none of it
 * can be worked out inside a command, because a command that computed its own order key would
 * compute a different one on replay. So it is worked out here, once, against the document as it
 * is now, and then frozen into the command.
 *
 * Not every command has a builder. One earns its place by computing something — an order key, a
 * side, rich text from a keystroke — or by refusing something the editor would otherwise have to
 * check for itself. The rest are one object literal, and a wrapper around an object literal is a
 * second place for the shape to be wrong.
 */
import { withCustomization } from '@orbit-mind/domain';
import { parseContent } from '@orbit-mind/domain';
import { withContent, withMapLink, canChangeSide, canReparent, childrenOf, getNode, orderAtEnd, orderAtIndex, planSideFlip, richTextFromPlain, subtree, toPlainText, } from '@orbit-mind/domain';
import { appError, err, ok, } from '@orbit-mind/shared-types';
/**
 * Where a new node appears.
 *
 * Not a layout — layouts arrive in steps 17 to 20 and will overwrite these. This is the
 * free-form starting position any editor has to give a node so it is somewhere the user can see
 * it, rather than at the origin on top of everything else.
 *
 * Computed here, against the document as it is now, and frozen into the command for the same
 * reason the order key is: a position recomputed at replay time would put the node wherever the
 * siblings happen to be *then*.
 */
const PLACEMENT = { gapX: 220, gapY: 56 };
function placeChild(document, parent, side) {
    const siblings = childrenOf(document, parent.id);
    // Left-side branches grow leftwards, so a two-sided map opens outwards from the root rather
    // than stacking everything to the right.
    const direction = side === 'left' ? -1 : 1;
    const x = parent.x + direction * PLACEMENT.gapX;
    if (siblings.length === 0)
        return { x, y: parent.y };
    // Below the lowest sibling. Crude, and deliberately so: anything cleverer here would be a
    // layout algorithm competing with the real ones.
    const lowest = Math.max(...siblings.map((sibling) => sibling.y));
    return { x, y: lowest + PLACEMENT.gapY };
}
/** Recovered edits use the same journal and undo path as typing. Invalid content is kept. */
export function restoreDraft(context, draft) {
    if (draft.field === 'map')
        return ok(renameMap(context, draft.text));
    const nodeId = draft.nodeId;
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    if (draft.field === 'node')
        return setText(context, { nodeId, text: draft.text });
    if (draft.field === 'notes')
        return ok(setNotes(context, nodeId, draft.text === '' ? null : richTextFromPlain(draft.text)));
    if (draft.field === 'tooltip')
        return setCustomization(context, nodeId, 'tooltip', draft.text);
    try {
        const content = parseContent(JSON.parse(draft.text));
        if (content !== undefined)
            return setContent(context, nodeId, content);
    }
    catch {
        /* Keep the original draft when a partial editor value cannot be applied. */
    }
    return err(appError('VALIDATION_FAILED', 'O conteúdo recuperado precisa ser revisado no editor.'));
}
function missing(nodeId) {
    return appError('NODE_NOT_FOUND', 'no such node in this map', {
        details: { nodeId },
        retryable: false,
    });
}
function requireNode(context, nodeId) {
    const node = getNode(context.document, nodeId);
    return node === undefined ? err(missing(nodeId)) : ok(node);
}
function command(kind, payload, meta) {
    return { kind, payload, meta };
}
/**
 * A new child at the end of a parent's children.
 *
 * The order key is computed here rather than in the command because it depends on the siblings
 * that exist *now*. Replay must not recompute it: by then the siblings may be different, and the
 * node would land somewhere the user never put it.
 */
export function createChild(context, input) {
    const parent = requireNode(context, input.parentId);
    if (!parent.ok)
        return parent;
    const siblings = childrenOf(context.document, input.parentId).map((node) => node.order);
    // Inherited from the parent unless the caller says otherwise, so a new child appears on the
    // side of the map its branch is already on.
    const side = input.side ?? parent.value.side;
    return ok(command('create-node', {
        nodeId: context.environment.ids.nodeId(),
        parentId: input.parentId,
        order: orderAtEnd(siblings),
        ...(input.text === undefined ? {} : { text: input.text }),
        side,
        position: placeChild(context.document, parent.value, side),
    }, context.meta));
}
/** A new sibling immediately after `siblingId`, which is what Enter does in an outline. */
export function createSibling(context, input) {
    const sibling = requireNode(context, input.siblingId);
    if (!sibling.ok)
        return sibling;
    const parentId = sibling.value.parentId;
    if (parentId === null) {
        // The root has no siblings, and a second root is not a thing a map can have.
        return err(appError('ROOT_IMMUTABLE', 'the root node cannot have a sibling', {
            details: { nodeId: input.siblingId },
            retryable: false,
        }));
    }
    const siblings = childrenOf(context.document, parentId);
    const index = siblings.findIndex((node) => node.id === input.siblingId);
    return ok(command('create-node', {
        nodeId: context.environment.ids.nodeId(),
        parentId,
        order: orderAtIndex(siblings.map((node) => node.order), index + 1),
        ...(input.text === undefined ? {} : { text: input.text }),
        side: sibling.value.side,
        // Directly below the sibling it follows, which is where the eye expects it.
        position: { x: sibling.value.x, y: sibling.value.y + PLACEMENT.gapY },
    }, context.meta));
}
/**
 * A new title for the map.
 *
 * Returns a command unconditionally rather than a `Result`: there is nothing to look up and
 * nothing to refuse here. Whether the title is *acceptable* is the command layer's question, and
 * it answers it with the same rule the entity schema will apply when the map is read back.
 */
export function renameMap(context, title) {
    return command('rename-map', { title }, context.meta);
}
export function deleteNode(context, nodeId) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    if (node.value.parentId === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root node cannot be deleted', {
            details: { nodeId },
            retryable: false,
        }));
    }
    return ok(command('delete-node', { nodeId }, context.meta));
}
/**
 * Replaces a node's text.
 *
 * Takes a plain string because that is what a text input produces. A caller that already has
 * rich text — a paste, a formatting toolbar — uses `setRichText` and keeps its marks.
 */
export function setText(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    return ok(command('update-node-text', { nodeId: input.nodeId, richText: richTextFromPlain(input.text) }, context.meta));
}
export function setRichText(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    return ok(command('update-node-text', { nodeId: input.nodeId, richText: input.richText }, context.meta));
}
/**
 * Places a node by hand.
 *
 * `pinned` defaults to true: a user who dragged a node meant it, and an automatic layout that
 * moves it back is the fastest way to lose their trust in the layout button.
 */
export function moveNode(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    return ok(command('move-node', { nodeId: input.nodeId, position: input.position, pinned: input.pinned ?? true }, context.meta));
}
/**
 * Reparents a node, optionally at a position among its new siblings.
 *
 * The cycle check is `canReparent`, which already exists in the domain — a drop that would make
 * a node its own ancestor detaches the subtree from the tree entirely, and the map is gone.
 */
export function reparentNode(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    const parent = requireNode(context, input.newParentId);
    if (!parent.ok)
        return parent;
    if (!canReparent(context.document, input.nodeId, input.newParentId)) {
        return err(appError('CYCLE_DETECTED', 'a node cannot be moved inside its own subtree', {
            details: { nodeId: input.nodeId, newParentId: input.newParentId },
            retryable: false,
        }));
    }
    const siblings = childrenOf(context.document, input.newParentId)
        .filter((sibling) => sibling.id !== input.nodeId)
        .map((sibling) => sibling.order);
    return ok(command('reparent-node', {
        nodeId: input.nodeId,
        newParentId: input.newParentId,
        order: input.index === undefined ? orderAtEnd(siblings) : orderAtIndex(siblings, input.index),
    }, context.meta));
}
/** Moves a node among its existing siblings. */
export function reorderNode(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    if (node.value.parentId === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root node has no siblings to reorder among', {
            details: { nodeId: input.nodeId },
            retryable: false,
        }));
    }
    const siblings = childrenOf(context.document, node.value.parentId)
        .filter((sibling) => sibling.id !== input.nodeId)
        .map((sibling) => sibling.order);
    return ok(command('reorder-node', { nodeId: input.nodeId, order: orderAtIndex(siblings, input.index) }, context.meta));
}
export function setCollapsed(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    return ok(command('set-collapsed', { nodeId: input.nodeId, collapsed: input.collapsed }, context.meta));
}
/**
 * Moves a whole branch to the other side of the root — MVP acceptance criterion 6.
 *
 * One command per node rather than one for the branch, because that is the command vocabulary
 * there is; they are dispatched together so they land in the journal as one transaction. The plan
 * comes from the domain's `planSideFlip`, so "which nodes move, and to what" is decided in one
 * place rather than here and again in the layout engine.
 */
export function flipBranchSide(context, branchId) {
    const node = requireNode(context, branchId);
    if (!node.ok)
        return node;
    if (!canChangeSide(context.document, branchId)) {
        return err(appError('VALIDATION_FAILED', 'this node cannot change side', {
            details: { nodeId: branchId },
            retryable: false,
        }));
    }
    const changes = planSideFlip(context.document, branchId);
    if (changes.length === 0)
        return ok([]);
    return ok(changes.map((change) => command('change-node-side', { nodeId: change.nodeId, side: change.to }, context.meta)));
}
/** A cross-link between two nodes. Hierarchy is `parentId`; this is everything else. */
export function connect(context, input) {
    const source = requireNode(context, input.sourceNodeId);
    if (!source.ok)
        return source;
    const target = requireNode(context, input.targetNodeId);
    if (!target.ok)
        return target;
    if (input.sourceNodeId === input.targetNodeId) {
        return err(appError('VALIDATION_FAILED', 'an edge must not connect a node to itself', {
            details: { nodeId: input.sourceNodeId },
            retryable: false,
        }));
    }
    return ok(command('create-edge', {
        edgeId: context.environment.ids.edgeId(),
        sourceNodeId: input.sourceNodeId,
        targetNodeId: input.targetNodeId,
        ...(input.label === undefined ? {} : { label: input.label }),
    }, context.meta));
}
/**
 * A node's emoji.
 *
 * `null` removes it, which is why this goes through the payload's `clear` list rather than
 * writing `undefined` into `changes`: a merge cannot express absence, and undoing "added an
 * emoji" has to leave the node with none rather than with an empty one.
 */
export function setEmoji(context, nodeId, emoji) {
    return command('update-node', emoji === null ? { nodeId, changes: {}, clear: ['emoji'] } : { nodeId, changes: { emoji } }, context.meta);
}
/**
 * What a node shows besides its words: a picture, a table, a chart, a drawing, a recording.
 *
 * One `update-node` over the whole `metadata` record, built from the node as it is now, so the
 * inverse puts back exactly what was there — including other keys a newer client may have put in
 * the same record. `null` takes the content away; a record left empty is cleared rather than
 * stored as `{}`.
 */
/** Existing metadata commands carry customization through undo, sync and backups. */
export function setCustomization(context, nodeId, key, value) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    return ok(command('update-node', { nodeId, changes: { metadata: withCustomization(node.value.metadata, key, value) } }, context.meta));
}
export function setContent(context, nodeId, content) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    const metadata = withContent(node.value.metadata, content);
    return ok(command('update-node', metadata === undefined
        ? { nodeId, changes: {}, clear: ['metadata'] }
        : { nodeId, changes: { metadata } }, context.meta));
}
/** Where a node leads: another map, opened from this topic. `null` removes the link. */
export function setMapLink(context, nodeId, link) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    const metadata = withMapLink(node.value.metadata, link);
    return ok(command('update-node', metadata === undefined
        ? { nodeId, changes: {}, clear: ['metadata'] }
        : { nodeId, changes: { metadata } }, context.meta));
}
/**
 * The long-form note behind a node.
 *
 * `notes` is `RichText` like the node's own text, and empty is not the same as absent: a note
 * somebody emptied should leave the node with no note at all, or the map keeps a marker for
 * something that is not there.
 */
export function setNotes(context, nodeId, notes) {
    const empty = notes !== null && toPlainText(notes).trim() === '';
    return command('update-node', notes === null || empty
        ? { nodeId, changes: {}, clear: ['notes'] }
        : { nodeId, changes: { notes } }, context.meta);
}
/**
 * A size the user chose.
 *
 * `pinned` rides along in the same command, and that is not tidiness: `dispatchAll` journals a
 * batch in one transaction but records each command as its *own* undo step, so a size and a pin
 * sent separately would take two presses of Ctrl+Z to take back — and the first one would be
 * labelled "undo moving a node", which is not what anybody did. One field of the node, one
 * command, one step.
 */
export function resizeNode(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    return ok(command('update-node', {
        nodeId: input.nodeId,
        changes: { width: input.width, height: input.height, pinned: true },
    }, context.meta));
}
/**
 * Giving a node its measured size back.
 *
 * Zero, not a guess: the scene falls back to measuring the text whenever a stored size is zero,
 * so clearing is how a node stops being hand-sized. The pin is *removed* rather than set to
 * false, because a node that has never been placed by hand does not carry the flag at all — and
 * "reset, then undo" has to arrive back at the document the user started with, not at one
 * carrying `pinned: false`.
 */
export function resetNodeSize(context, nodeId) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    return ok(command('update-node', { nodeId, changes: { width: 0, height: 0 }, clear: ['pinned'] }, context.meta));
}
/** How far a duplicate is offset from its original, in map units. */
const DUPLICATE_OFFSET = 24;
/**
 * A copy of a subtree, ready to be planted somewhere.
 *
 * Every node gets a fresh id, and so does every cross-link *between* copied nodes — a link with
 * one end outside the copy is dropped rather than duplicated, because a pasted branch pointing at
 * the original's neighbour is a relationship nobody drew.
 *
 * The root of the copy is first in the array, which is what `restore-nodes` requires: it is the
 * only node whose parent has to already exist, and it is the node whose deletion undoes the whole
 * paste in one step.
 */
export function duplicateSubtree(context, input) {
    const source = requireNode(context, input.nodeId);
    if (!source.ok)
        return source;
    const target = requireNode(context, input.parentId);
    if (!target.ok)
        return target;
    const originals = subtree(context.document, input.nodeId, { includeCollapsed: true });
    const within = new Set(originals.map((node) => node.id));
    const edges = Object.values(context.document.edges).filter((edge) => within.has(edge.sourceNodeId) && within.has(edge.targetNodeId));
    return ok(plant(context, originals, edges, input.parentId));
}
/**
 * Plants a clipping under a node.
 *
 * Every id is minted fresh, so a clipping can be pasted as many times as somebody presses the
 * key. The nodes are re-parented onto each other by their *copied* ids, and the first one — the
 * clipping's own root — is re-parented onto the target.
 */
export function pasteSubtree(context, input) {
    const target = requireNode(context, input.parentId);
    if (!target.ok)
        return target;
    if (input.clipping.nodes.length === 0) {
        return err(appError('VALIDATION_FAILED', 'there is nothing to paste', { retryable: false }));
    }
    return ok(plant(context, input.clipping.nodes, input.clipping.edges, input.parentId));
}
/**
 * The shared half of duplicating and pasting.
 *
 * Cross-links are copied only when *both* ends are inside the clipping. A link with one end
 * outside it would attach the copy to the original's neighbour, which is a relationship nobody
 * drew — and after a cut, to a node that is not there at all.
 */
function plant(context, originals, edges, parentId) {
    const ids = new Map();
    for (const node of originals)
        ids.set(node.id, context.environment.ids.nodeId());
    const siblings = childrenOf(context.document, parentId).map((sibling) => sibling.order);
    const copies = originals.map((node, index) => ({
        ...node,
        id: requireCopy(ids, node.id),
        // The clipping's own root reattaches to the target; everything else follows its copied parent.
        parentId: index === 0 ? parentId : requireCopy(ids, requireParent(node)),
        order: index === 0 ? orderAtEnd(siblings) : node.order,
        // Offset a little, so a copy is a second thing on screen rather than one hidden exactly under
        // the first until the next arrange separates them.
        x: node.x + DUPLICATE_OFFSET,
        y: node.y + DUPLICATE_OFFSET,
        createdAt: context.meta.timestamp,
        createdBy: context.meta.authorId,
        updatedAt: context.meta.timestamp,
        updatedBy: context.meta.authorId,
    }));
    const copiedEdges = edges
        .filter((edge) => ids.has(edge.sourceNodeId) && ids.has(edge.targetNodeId))
        .map((edge) => ({
        ...edge,
        id: context.environment.ids.edgeId(),
        sourceNodeId: requireCopy(ids, edge.sourceNodeId),
        targetNodeId: requireCopy(ids, edge.targetNodeId),
        createdAt: context.meta.timestamp,
        updatedAt: context.meta.timestamp,
    }));
    return command('restore-nodes', { nodes: copies, edges: copiedEdges }, context.meta);
}
/**
 * The subtree under a node, as a clipping.
 *
 * Read here rather than in the client, because "what belongs to this node" is a question about
 * the document and the answer has to match what a paste will plant.
 */
export function clip(context, nodeId) {
    const node = requireNode(context, nodeId);
    if (!node.ok)
        return node;
    const nodes = subtree(context.document, nodeId, { includeCollapsed: true });
    const within = new Set(nodes.map((candidate) => candidate.id));
    return ok({
        nodes,
        edges: Object.values(context.document.edges).filter((edge) => within.has(edge.sourceNodeId) && within.has(edge.targetNodeId)),
    });
}
function requireCopy(ids, original) {
    const copy = ids.get(original);
    if (copy === undefined)
        throw new Error(`no copy was minted for node ${original}`);
    return copy;
}
function requireParent(node) {
    if (node.parentId === null)
        throw new Error(`node ${node.id} has no parent to copy under`);
    return node.parentId;
}
/**
 * Wrapping a selection in a group node.
 *
 * The members have to be siblings, and that is the model rather than a limitation: a group is a
 * node the members hang from, so nodes with different parents cannot share one without one of
 * them leaving the branch it belongs to.
 *
 * Order keys are left alone. The members were already ordered relative to each other, and minting
 * new ones would reshuffle a selection somebody made deliberately.
 */
export function groupNodes(context, nodeIds, title) {
    if (nodeIds.length < 2) {
        return err(appError('VALIDATION_FAILED', 'grouping needs at least two nodes', { retryable: false }));
    }
    const first = context.document.nodes[nodeIds[0] ?? ''];
    if (first === undefined) {
        return err(appError('NODE_NOT_FOUND', `node not found: ${nodeIds[0] ?? ''}`));
    }
    const parentId = first.parentId;
    if (parentId === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root cannot be grouped', { retryable: false }));
    }
    const shareAParent = nodeIds.every((id) => context.document.nodes[id]?.parentId === parentId);
    if (!shareAParent) {
        return err(appError('VALIDATION_FAILED', 'a group can only hold nodes that are already siblings', {
            retryable: false,
        }));
    }
    return ok(command('group-nodes', {
        groupId: context.environment.ids.nodeId(),
        nodeIds,
        order: orderAtEnd(childrenOf(context.document, parentId).map((sibling) => sibling.order)),
        ...(title === undefined ? {} : { title }),
    }, context.meta));
}
/**
 * Taking a group apart.
 *
 * The members are handed order keys among the *grandparent's* children, minted here because that
 * is what depends on the document as it is now — replay must not recompute them, or the freed
 * nodes land somewhere the user never put them.
 */
export function ungroupNodes(context, groupId) {
    const group = requireNode(context, groupId);
    if (!group.ok)
        return group;
    const grandparent = group.value.parentId;
    if (grandparent === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root node is not a group', { retryable: false }));
    }
    const members = childrenOf(context.document, groupId);
    const taken = childrenOf(context.document, grandparent)
        .filter((sibling) => sibling.id !== groupId)
        .map((sibling) => sibling.order);
    const orders = {};
    for (const member of members) {
        const next = orderAtEnd(taken);
        orders[member.id] = next;
        taken.push(next);
    }
    return ok(command('ungroup-nodes', { groupId, orders }, context.meta));
}
/**
 * A node's own colour and shape.
 *
 * `null` means "clear this override", and clearing is why the command is sent with `replace`
 * rather than as a merge: a partial patch cannot express the absence of a key, so merging
 * `{ fill: undefined }` over the old style would leave the old fill exactly where it was. The
 * style is therefore rebuilt here, in full, and sent as the whole truth.
 *
 * A node with no fill of its own takes its colour from its depth, which is what makes a branch
 * look like a branch. Clearing is the common case — somebody trying a colour and changing their
 * mind — so it has to work as well as setting does.
 */
export function setStyle(context, input) {
    const node = requireNode(context, input.nodeId);
    if (!node.ok)
        return node;
    let style = node.value.style;
    if (input.fill !== undefined)
        style = withOrWithout(style, 'fill', input.fill);
    if (input.stroke !== undefined)
        style = withOrWithout(style, 'stroke', input.stroke);
    return ok(command('update-node-style', {
        nodeId: input.nodeId,
        style,
        replace: true,
        ...(input.shape === undefined ? {} : { shape: input.shape }),
    }, context.meta));
}
/** Sets a colour on the style, or removes it when the value is `null`. */
function withOrWithout(style, key, value) {
    if (value === null) {
        const { [key]: _removed, ...rest } = style;
        return rest;
    }
    return { ...style, [key]: value };
}
/**
 * Changing a connection: its label, how it is drawn, what its ends carry.
 *
 * A plain `Command` rather than a `Result`, like `disconnect`: there is nothing to look up here,
 * and an edge that has gone is refused by the command layer with the id in the message.
 *
 * What this cannot do is *remove* a field. `update-edge` merges its changes, and a merge has no
 * way to say "this key was absent" — so a colour, once set, can be changed but not cleared back
 * to the accent. Expressing that needs a `clear` list on the payload, and it is not worth a
 * schema and a drift assertion until something asks for it.
 */
export function updateEdge(context, input) {
    return command('update-edge', { edgeId: input.edgeId, changes: input.changes }, context.meta);
}
export function disconnect(context, edgeId) {
    return command('delete-edge', { edgeId }, context.meta);
}
//# sourceMappingURL=intents.js.map